/**
 * The finite chart grammar (#1188).
 *
 * This is the stored contract, not a renderer's options object. A definition
 * names governed semantic sources and encodings; hosts resolve those names to
 * data the caller may already read. URLs, SQL, callbacks and library-specific
 * configuration have nowhere to enter the shape.
 */

import { isRecord as object, unknownKeys } from './documentShape.js'

export const CHART_DEFINITION_VERSION = 1 as const

/** One 744-bucket Timeline with the governed maximum of four series. */
export const CHART_MAX_ROWS = 2_976 as const

export const CHART_KINDS = [
  'kpi', 'sparkline', 'line', 'area', 'bar', 'stacked_bar', 'bullet',
  'waterfall', 'scatter', 'heatmap', 'funnel',
] as const
export type ChartKind = typeof CHART_KINDS[number]

export const TIME_GRAINS = ['hour', 'day', 'week', 'month', 'quarter', 'year'] as const
export type TimeGrain = typeof TIME_GRAINS[number]

export const CHART_VALUE_FORMATS = ['number', 'currency', 'percent', 'duration'] as const
export type ChartValueFormat = typeof CHART_VALUE_FORMATS[number]

export const DASHBOARD_SIZE_HINTS = ['small', 'medium', 'full'] as const
export type DashboardSizeHint = typeof DASHBOARD_SIZE_HINTS[number]

export interface ChartEncoding {
  measure: string
  dimension?: string
  series?: string
  timeField?: string
  timeGrain?: TimeGrain
  comparisonMeasure?: string
  baselineMeasure?: string
}

export interface ChartValueFormatting {
  kind: ChartValueFormat
  currency?: string
  precision?: number
}

export interface ChartFreshness {
  /** Semantic result field, not a database expression. */
  observedAtField: string
  staleAfterMinutes: number
}

export interface ChartDefinition {
  definitionVersion: typeof CHART_DEFINITION_VERSION
  key: string
  revision: number
  kind: ChartKind
  /** Opaque governed source key supplied by the host. */
  source: string
  encoding: ChartEncoding
  title: string
  intent: string
  description: string
  emptyState: string
  format?: ChartValueFormatting
  freshness?: ChartFreshness
  sort?: { by: 'measure' | 'dimension' | 'time'; direction: 'asc' | 'desc' }
  limit?: number
  /** Opaque action key. The host still resolves authority at activation. */
  drillAction?: string
}

export interface DashboardSlotDefinition {
  itemKey: string
  order: number
  sizeHint: DashboardSizeHint
  group?: string
}

const IDENTIFIER = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const CURRENCY = /^[A-Z]{3}$/u
const UNSAFE_TEXT = /(?:https?:\/\/|javascript:|data:text\/html)/iu
const TOP_KEYS = new Set([
  'definitionVersion', 'key', 'revision', 'kind', 'source', 'encoding', 'title',
  'intent', 'description', 'emptyState', 'format', 'freshness', 'sort', 'limit',
  'drillAction',
])
const ENCODING_KEYS = new Set([
  'measure', 'dimension', 'series', 'timeField', 'timeGrain',
  'comparisonMeasure', 'baselineMeasure',
])
const FORMAT_KEYS = new Set(['kind', 'currency', 'precision'])
const FRESHNESS_KEYS = new Set(['observedAtField', 'staleAfterMinutes'])
const SORT_KEYS = new Set(['by', 'direction'])
const SLOT_KEYS = new Set(['itemKey', 'order', 'sizeHint', 'group'])

const requireIdentifier = (value: unknown, at: string, issues: string[]) => {
  if (typeof value !== 'string' || !IDENTIFIER.test(value)) issues.push(`${at} must be an opaque identifier`)
}

const requireCopy = (value: unknown, at: string, issues: string[]) => {
  if (typeof value !== 'string' || value.trim().length === 0) issues.push(`${at} must be non-empty text`)
  else if (UNSAFE_TEXT.test(value)) issues.push(`${at} must not contain a URL or executable scheme`)
}

/** Validate definitions loaded from DATA or proposed through copilot/MCP. */
export function validateChartDefinition(value: unknown): string[] {
  if (!object(value)) return ['definition must be an object']
  const issues = unknownKeys(value, TOP_KEYS, 'definition')
  if (value.definitionVersion !== CHART_DEFINITION_VERSION) issues.push(`definitionVersion must be ${CHART_DEFINITION_VERSION}`)
  requireIdentifier(value.key, 'key', issues)
  requireIdentifier(value.source, 'source', issues)
  if (!Number.isSafeInteger(value.revision) || Number(value.revision) < 1) issues.push('revision must be a positive integer')
  if (typeof value.kind !== 'string' || !CHART_KINDS.includes(value.kind as ChartKind)) issues.push('kind is not shipped by this grammar version')
  for (const key of ['title', 'intent', 'description', 'emptyState'] as const) requireCopy(value[key], key, issues)

  if (!object(value.encoding)) issues.push('encoding must be an object')
  else {
    issues.push(...unknownKeys(value.encoding, ENCODING_KEYS, 'encoding'))
    requireIdentifier(value.encoding.measure, 'encoding.measure', issues)
    for (const key of ['dimension', 'series', 'timeField', 'comparisonMeasure', 'baselineMeasure'] as const) {
      if (value.encoding[key] !== undefined) requireIdentifier(value.encoding[key], `encoding.${key}`, issues)
    }
    if (value.encoding.timeGrain !== undefined && !TIME_GRAINS.includes(value.encoding.timeGrain as TimeGrain)) {
      issues.push('encoding.timeGrain is not supported')
    }
    if ((value.encoding.timeField === undefined) !== (value.encoding.timeGrain === undefined)) {
      issues.push('encoding.timeField and encoding.timeGrain must be supplied together')
    }
  }

  if (value.format !== undefined) {
    if (!object(value.format)) issues.push('format must be an object')
    else {
      issues.push(...unknownKeys(value.format, FORMAT_KEYS, 'format'))
      if (!CHART_VALUE_FORMATS.includes(value.format.kind as ChartValueFormat)) issues.push('format.kind is not supported')
      if (value.format.currency !== undefined && (typeof value.format.currency !== 'string' || !CURRENCY.test(value.format.currency))) {
        issues.push('format.currency must be an ISO 4217 code')
      }
      if (value.format.kind === 'currency' && value.format.currency === undefined) issues.push('format.currency is required for currency values')
      if (value.format.kind !== 'currency' && value.format.currency !== undefined) issues.push('format.currency only belongs to currency values')
      if (value.format.precision !== undefined && (!Number.isInteger(value.format.precision) || Number(value.format.precision) < 0 || Number(value.format.precision) > 6)) {
        issues.push('format.precision must be an integer from 0 to 6')
      }
    }
  }

  if (value.freshness !== undefined) {
    if (!object(value.freshness)) issues.push('freshness must be an object')
    else {
      issues.push(...unknownKeys(value.freshness, FRESHNESS_KEYS, 'freshness'))
      requireIdentifier(value.freshness.observedAtField, 'freshness.observedAtField', issues)
      if (!Number.isInteger(value.freshness.staleAfterMinutes) || Number(value.freshness.staleAfterMinutes) < 1) {
        issues.push('freshness.staleAfterMinutes must be a positive integer')
      }
    }
  }

  if (value.sort !== undefined) {
    if (!object(value.sort)) issues.push('sort must be an object')
    else {
      issues.push(...unknownKeys(value.sort, SORT_KEYS, 'sort'))
      if (!['measure', 'dimension', 'time'].includes(String(value.sort.by))) issues.push('sort.by is not supported')
      if (!['asc', 'desc'].includes(String(value.sort.direction))) issues.push('sort.direction is not supported')
    }
  }
  if (value.limit !== undefined && (!Number.isInteger(value.limit) || Number(value.limit) < 1 || Number(value.limit) > CHART_MAX_ROWS)) {
    issues.push(`limit must be an integer from 1 to ${CHART_MAX_ROWS}`)
  }
  if (value.drillAction !== undefined) requireIdentifier(value.drillAction, 'drillAction', issues)
  return issues
}

/** Ordered responsive slots: deliberately no x/y coordinates or pixel sizes. */
export function validateDashboardSlots(value: unknown): string[] {
  if (!Array.isArray(value)) return ['slots must be an array']
  const issues: string[] = []
  const items = new Set<string>()
  const orders = new Set<number>()
  value.forEach((slot, index) => {
    if (!object(slot)) { issues.push(`slots[${index}] must be an object`); return }
    issues.push(...unknownKeys(slot, SLOT_KEYS, `slots[${index}]`))
    requireIdentifier(slot.itemKey, `slots[${index}].itemKey`, issues)
    if (typeof slot.itemKey === 'string' && items.has(slot.itemKey)) issues.push(`item ${slot.itemKey} is duplicated`)
    else if (typeof slot.itemKey === 'string') items.add(slot.itemKey)
    if (!Number.isSafeInteger(slot.order) || Number(slot.order) < 0) issues.push(`slots[${index}].order must be a non-negative integer`)
    else if (orders.has(Number(slot.order))) issues.push(`order ${String(slot.order)} is duplicated`)
    else orders.add(Number(slot.order))
    if (!DASHBOARD_SIZE_HINTS.includes(slot.sizeHint as DashboardSizeHint)) issues.push(`slots[${index}].sizeHint is not supported`)
    if (slot.group !== undefined) requireIdentifier(slot.group, `slots[${index}].group`, issues)
  })
  return issues
}

/**
 * Validate a chart definition AND bind it to the source the widget names
 * (ROOT 68246, moved here from `apps/portal/src/data.tsx`).
 *
 * The binding is the half that grammar checking cannot do: a definition may be
 * perfectly valid for a DIFFERENT registered door, and rendering it against
 * this one would label one source as another. It lives beside
 * `validateChartDefinition` because the two are one decision, and it stays in
 * Experience because `@solum/sdk` must not depend on this package — Experience
 * already depends on the SDK, and the executor there returns rows with no
 * opinion about what a chart means.
 */
export function parseDashboardChartDefinition(value: unknown, sourceKey: string): ChartDefinition {
  const issues = validateChartDefinition(value)
  if (issues.length > 0) throw new Error(`This chart definition is not valid: ${issues.join('; ')}`)
  const definition = value as ChartDefinition
  if (definition.source !== sourceKey) {
    throw new Error(`This chart definition names source ${definition.source}, not ${sourceKey}.`)
  }
  return definition
}
